import * as React from "react"
import { AlertDialog as AlertDialogPrimitive } from "@base-ui/react/alert-dialog"
import { Button } from "@/primitives/button"
import { BottomSheetHandle } from "@/primitives/drawer"
import { useBackDismiss } from "@/design-system/use-back-dismiss.ts"
import { useOverlayForm } from "@/design-system/use-overlay-form.ts"
import { cn } from "@/primitives/utils"

/*
 * beacon: an alert chooses its form as it opens (`useOverlayForm`) and keeps it while open. On the
 * desk it is the centred alert below. On `compact` it stays a centred alert, no taller than the
 * screen; but one asking for something typed or chosen (`docked` on its content, as a Confirmation
 * with a `choice` sets) docks as a bottom sheet riding the keyboard, under the sheets' handle, so
 * the field and its Confirm stay in view. Either form stacks its actions full width with the
 * confirming one on top. Either form
 * cancels on Back, as a phone's own alerts do, rather than leaving the page under it, unless it
 * answers a router's navigation blocker (`backDismiss={false}`): the router is already putting
 * the history back, and an entry pushed while it does would lose the step it is waiting for. It
 * is no Drawer: an alert isn't dragged or tapped away.
 */
type AlertForm = { compact: boolean; open: boolean; close: () => void }

const AlertFormContext = React.createContext<AlertForm>({
  compact: false,
  open: false,
  close: () => {},
})

function AlertDialog({ actionsRef, ...props }: AlertDialogPrimitive.Root.Props) {
  const ownActions = React.useRef<AlertDialogPrimitive.Root.Actions>(null)
  const actions = actionsRef ?? ownActions
  const { open, compact, onOpenChange } = useOverlayForm(props)
  const form = React.useMemo(
    () => ({ compact, open, close: () => actions.current?.close() }),
    [compact, open, actions],
  )
  return (
    <AlertFormContext value={form}>
      <AlertDialogPrimitive.Root
        data-slot="alert-dialog"
        actionsRef={actions}
        {...props}
        onOpenChange={onOpenChange}
      />
    </AlertFormContext>
  )
}

function AlertDialogTrigger({ ...props }: AlertDialogPrimitive.Trigger.Props) {
  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
}

function AlertDialogPortal({ ...props }: AlertDialogPrimitive.Portal.Props) {
  return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />
}

function AlertDialogOverlay({ className, ...props }: AlertDialogPrimitive.Backdrop.Props) {
  return (
    <AlertDialogPrimitive.Backdrop
      data-slot="alert-dialog-overlay"
      className={cn(
        // beacon: the scrim token, with no blur, as Dialog's.
        "fixed inset-0 isolate z-50 bg-scrim duration-150 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:duration-100 data-closed:fade-out-0",
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogContent({
  className,
  size = "default",
  docked: docks = false,
  backDismiss = true,
  initialFocus,
  children,
  ...props
}: AlertDialogPrimitive.Popup.Props & {
  size?: "default" | "sm"
  /** beacon: it asks for something typed or chosen, so it docks on `compact`. */
  docked?: boolean
  /** beacon: Back cancels it on `compact`; false where a navigation blocker opens it. */
  backDismiss?: boolean
}) {
  const popup = React.useRef<HTMLDivElement>(null)
  const form = React.use(AlertFormContext)
  const docked = docks && form.compact
  useBackDismiss(form.open && form.compact && backDismiss, form.close)
  return (
    <AlertDialogPortal>
      {/* beacon: docked, the scrim fades on the sheet's motion. */}
      <AlertDialogOverlay className={cn(docked && "duration-200 data-closed:duration-150")} />
      <AlertDialogPrimitive.Popup
        ref={popup}
        data-slot="alert-dialog-content"
        data-size={size}
        data-docked={docked || undefined}
        data-bottom-sheet={docked || undefined}
        // beacon: the confirming action takes focus as the dialog opens, so Enter confirms; the
        // choice is on screen and Esc still cancels.
        initialFocus={
          initialFocus ??
          (() =>
            popup.current?.querySelector<HTMLElement>(
              "[data-slot=alert-dialog-action]:not(:disabled)",
            ) ?? true)
        }
        className={cn(
          docked
            ? // beacon: a bottom sheet on the keyboard, its top corners rounded, clear of the home
              // indicator, scrolling within what the keyboard leaves of the screen: every bottom
              // sheet's geometry and motion (`tokens.css`, the Drawer).
              "group/alert-dialog-content fixed inset-x-0 bottom-keyboard z-50 mx-auto grid max-h-sheet w-full max-w-xl gap-4 overflow-y-auto rounded-t-xl bg-popover p-5 pt-0 pb-sheet-5 text-ui text-popover-foreground shadow-dialog duration-200 ease-snappy outline-none data-open:animate-in data-open:slide-in-from-bottom data-closed:animate-out data-closed:duration-150 data-closed:slide-out-to-bottom"
            : // beacon: Dialog's surface and entrance, 20px padding and 16px between its parts.
              "group/alert-dialog-content fixed top-1/2 left-1/2 z-50 grid w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-5 text-ui text-popover-foreground shadow-dialog duration-150 ease-snappy outline-none data-[size=default]:max-w-md data-[size=sm]:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-[0.98] data-closed:animate-out data-closed:duration-100 data-closed:fade-out-0 data-closed:zoom-out-[0.98]",
          // beacon: on `compact`, the centred alert scrolls within the screen, less 1rem each side.
          form.compact && !docked && "max-h-[calc(100dvh-2rem)] overflow-y-auto",
          className,
        )}
        {...props}
      >
        {/* beacon: docked, the handle every bottom sheet carries, though an alert isn't dragged. */}
        {docked && <BottomSheetHandle className="sticky top-0 z-10 -mx-5 -mb-3 bg-popover" />}
        {children}
      </AlertDialogPrimitive.Popup>
    </AlertDialogPortal>
  )
}

function AlertDialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn(
        // beacon: left-aligned; the media tile, when there is one, sits above the title.
        "flex min-w-0 flex-col items-start gap-1 text-left",
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  const { compact } = React.use(AlertFormContext)
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        // beacon: actions at the end, with no rule above them. On `compact` they stack full width
        // with the confirming one (last in the markup) on top, in a centred alert and a docked
        // sheet alike.
        "flex items-center justify-end gap-2",
        compact && "flex-col-reverse items-stretch",
        className,
      )}
      {...props}
    />
  )
}

const alertDialogMediaTones = {
  destructive: "bg-destructive/10 text-destructive",
  brand: "bg-primary/10 text-brand-text",
} as const

/**
 * beacon: a 36px tile holding the case's icon, tinted by what confirming it does: `destructive`
 * removes something, `brand` grants or archives something consequential.
 */
function AlertDialogMedia({
  className,
  tone = "destructive",
  ...props
}: React.ComponentProps<"div"> & { tone?: keyof typeof alertDialogMediaTones }) {
  return (
    <div
      data-slot="alert-dialog-media"
      data-tone={tone}
      className={cn(
        "mb-3 inline-flex size-9 shrink-0 items-center justify-center rounded-lg *:[svg:not([class*='size-'])]:size-4.5",
        alertDialogMediaTones[tone],
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn(
        // beacon: a title in the type scale.
        "font-heading text-title font-semibold",
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn(
        // beacon: UI text in the secondary tier.
        "text-ui text-pretty text-muted-foreground *:[a]:text-brand-text *:[a]:underline-offset-3 *:[a]:hover:underline",
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogAction({ className, ...props }: React.ComponentProps<typeof Button>) {
  return <Button data-slot="alert-dialog-action" className={cn(className)} {...props} />
}

function AlertDialogCancel({
  className,
  variant = "outline",
  size = "default",
  ...props
}: AlertDialogPrimitive.Close.Props &
  Pick<React.ComponentProps<typeof Button>, "variant" | "size">) {
  return (
    <AlertDialogPrimitive.Close
      data-slot="alert-dialog-cancel"
      className={cn(className)}
      render={<Button variant={variant} size={size} />}
      {...props}
    />
  )
}

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
}
